import {
  Button,
  CheckInput,
  addToast,
  Select,
  SelectOptionType,
  TextInput,
} from '@octano/global-ui';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { ModalConfirmation } from '../../../../../../components/modals/Confirmation';
import { SectionTitle } from '../../../../../../components/text';
import { useRequirementTypeConfig } from '../../../../../../hooks/useRequirementTypeConfig';
import { useValidations } from '../../../../../../hooks/useValidations';
import { isNumber } from '../../../../../../utils/math';
import {
  getDegreeActivityByCurriculum,
  postCreateDegreeActivity,
} from '../../../api/study-plan-curriculums';
import { getStudyPlanRequest } from '../../../api/study-plan';
import { transformData, transformDataActivity } from './dataTransformation';
import { CreateActivityModal } from './parts/CreateActivity/CreateActivityModal';
import TableCurriculumActivity from './parts/TableCurriculumActivity';
import { DeleteActivityParams, TableCurriculumDegreeActivity } from './types';

export const prefix = 'studyPlans.curriculum.degreeActivities';
const ITEMS_PER_PAGE = 10;

interface CurriculumDegreeActivitiesListProps {
  readOnly: boolean;
  curriculumId?: string;
  studyPlanId?: string;
}

export interface StudyPlanTitlesAndDegrees {
  degreeTitle: string | null;
  academicDegree: string | null;
}

export enum DegreeActivitiesInCurriculumTypeOptions {
  None = 'none',
  Normal = 'normal',
  Grouped = 'grouped',
}
export const DEGREE_ACTIVITIES_IN_CURRICULUM_TYPE_OPTIONS: SelectOptionType[] = [
  {
    value: DegreeActivitiesInCurriculumTypeOptions.None,
    label: 'No mostrar en la malla',
  },
  {
    value: DegreeActivitiesInCurriculumTypeOptions.Normal,
    label: 'Mostrar cada Actividad en la malla',
  },
  {
    value: DegreeActivitiesInCurriculumTypeOptions.Grouped,
    label: 'Mostrar como grupo',
  },
];

export const CurriculumDegreeActivitiesList = ({
  readOnly,
  curriculumId,
  studyPlanId,
}: CurriculumDegreeActivitiesListProps) => {
  const { t } = useTranslation();
  const requirementTypeConfig = useRequirementTypeConfig();
  const [
    studyPlanTitles,
    setStudyPlanTitles,
  ] = useState<StudyPlanTitlesAndDegrees>({
    degreeTitle: null,
    academicDegree: null,
  });

  useEffect(() => {
    if (!studyPlanId) {
      return;
    }
    const numericId = Number(studyPlanId);
    if (!isNumber(numericId)) {
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await getStudyPlanRequest(numericId);
      if (cancelled || !data) {
        return;
      }
      setStudyPlanTitles({
        degreeTitle: data.data.degreeTitle ?? null,
        academicDegree: data.data.academicDegree ?? null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [studyPlanId]);
  const [
    curriculumDegreeActivityCode,
    setCurriculumDegreeActivityCode,
  ] = useState<number | string>('');

  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false);
  const [isLoadingTable, setIsTableLoading] = useState<boolean>(false);

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const [
    curriculumActivities,
    hasActivitiesChecked,
    degreeActivitiesInCurriculumType,
  ]: [
    curriculumActivities: TableCurriculumDegreeActivity[],
    hasActivitiesChecked: boolean,
    degreeActivitiesInCurriculumType: SelectOptionType,
  ] = watch([
    'curriculumActivities',
    'hasActivitiesChecked',
    'degreeActivitiesInCurriculumType',
  ]);

  const [totalPages, setTotalPages] = useState<number>(0);
  const [
    totalCurriculumDegreeActivities,
    setTotalCurriculumDegreeActivities,
  ] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const handleGetData = useCallback(
    async ({
      curriculumId,
      page = 0,
    }: {
      curriculumId: string;
      page: number;
    }) => {
      setIsTableLoading(true);
      const { data, error } = await getDegreeActivityByCurriculum(
        curriculumId,
        {
          items: ITEMS_PER_PAGE,
          page: page,
        },
      );

      if (error) {
        addToast({
          text: t(`${prefix}.errorOnSearch`),
          color: 'danger',
          icon: 'error',
        });
      }

      if (data) {
        const newDataTable = data?.data ? transformData(data?.data) : [];
        setValue('hasActivitiesChecked', !newDataTable.length);
        setValue('curriculumActivities', newDataTable);
        setTotalPages(data?.total_pages || 0);
        setTotalCurriculumDegreeActivities(data?.total || 0);
      }
      setIsTableLoading(false);
    },
    [t, setValue],
  );

  const handleSaveActivity = useCallback(
    async ({ searchParams }: any) => {
      const existCurriculumDegreeActivity = curriculumActivities.some(
        (data) => data.code === searchParams.code,
      );
      if (existCurriculumDegreeActivity) {
        addToast({
          text: t(`${prefix}.errorOnCreateRepeatActivity`),
          color: 'danger',
          icon: 'error',
        });
        return;
      }

      if (!searchParams.id) {
        const { data, error } = await postCreateDegreeActivity(searchParams);

        if (error) {
          addToast({
            text: t(`${prefix}.errorOnCreate`),
            color: 'danger',
            icon: 'error',
          });
          return;
        }

        if (data) {
          addToast({
            icon: 'success',
            color: 'success',
            text: t(`${prefix}.successfullyOnCreate`),
          });
          setShowModal(false);
          const newActivity = transformDataActivity({
            ...data,
            belongsTo: searchParams.belongsTo ?? null,
            requirementType: searchParams.requirementType ?? 'fixed',
          });
          setValue('curriculumActivities', [
            ...curriculumActivities,
            newActivity,
          ]);
          return;
        }
      }

      if (searchParams.id && searchParams) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`${prefix}.successfullyOnCreate`),
        });
        setShowModal(false);
        const newActivity = transformDataActivity(searchParams);
        setValue('curriculumActivities', [
          ...curriculumActivities,
          newActivity,
        ]);
        return;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, curriculumActivities],
  );

  const handleRemoveActivity = useCallback(
    async ({ curriculumDegreeActivityCode }: DeleteActivityParams) => {
      if (curriculumDegreeActivityCode) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`${prefix}.successfullyOnDelete`),
        });
        setShowModal(false);
        const newData = curriculumActivities.filter(
          (data) => data.code !== curriculumDegreeActivityCode,
        );
        setValue('curriculumActivities', newData);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, curriculumActivities],
  );

  useEffect(() => {
    if (!isNumber(Number(curriculumId))) {
      setValue('hasActivitiesChecked', false);
      setValue('curriculumActivities', []);
      return;
    }
    handleGetData({ curriculumId: curriculumId as string, page: currentPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curriculumId, currentPage]);

  useEffect(() => {
    if (errors.curriculumActivities) {
      const percentageSum = curriculumActivities.reduce(
        (sum, activity) => sum + (Number(activity.percentage) || 0),
        0,
      );
      if (percentageSum === 100) {
        setValue('curriculumActivities', curriculumActivities, {
          shouldValidate: true,
        });
      }
    }
  }, [setValue, curriculumActivities, errors.curriculumActivities]);

  useEffect(() => {
    if (hasActivitiesChecked) {
      setValue(
        'degreeActivitiesInCurriculumType',
        DEGREE_ACTIVITIES_IN_CURRICULUM_TYPE_OPTIONS.find(
          (d) => d.value === DegreeActivitiesInCurriculumTypeOptions.None,
        ),
      );
    }
  }, [setValue, hasActivitiesChecked]);

  const { msgValidations, validateTextNotEmpty } = useValidations();

  useEffect(() => {
    if (
      degreeActivitiesInCurriculumType?.value !==
      DegreeActivitiesInCurriculumTypeOptions.Grouped
    ) {
      setValue('degreeActivitiesInCurriculumGroup', '');
    }
  }, [setValue, degreeActivitiesInCurriculumType]);

  return (
    <Suspense fallback={'loading...'}>
      <div className="legend-table pb-5">
        <Row>
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row className="pb-3">
          <Col xs={12}>
            <span className="fs-18">{t(`${prefix}.subtitle`)}</span>
          </Col>
        </Row>

        {!readOnly && (
          <Row className="pb-0 mt-4">
            {!readOnly && curriculumActivities.length === 0 && (
              <Col xs="12" md={8} className="align-self-center">
                <CheckInput
                  name="hasActivitiesChecked"
                  value={hasActivitiesChecked}
                  label={t(`${prefix}.btn.checkedHasActivities`)}
                  onChange={() => {
                    setValue('hasActivitiesChecked', !hasActivitiesChecked);
                  }}
                />
              </Col>
            )}
            <Col xs={12} md={4} className="ml-auto mb-4">
              <Button
                className="ml-auto btn-block"
                size="md"
                text={t(`${prefix}.btn.insertActivity`)}
                icon="plus"
                onClick={() => setShowModal(!showModal)}
                disabled={hasActivitiesChecked}
              />
            </Col>
            <CreateActivityModal
              show={showModal}
              toggleModal={() => {
                setShowModal(!showModal);
              }}
              onSave={(searchParams: any) =>
                handleSaveActivity({ searchParams })
              }
              titleEnabled={studyPlanTitles.degreeTitle != null}
              degreeEnabled={studyPlanTitles.academicDegree != null}
              requirementTypeAvailables={requirementTypeConfig.availables}
              requirementTypeDefault={requirementTypeConfig.default}
            />
          </Row>
        )}

        <Row>
          <Col xs={12} md={6}>
            <Select
              label={t(`${prefix}.degreeActivitiesInCurriculumType`)}
              control={control}
              options={DEGREE_ACTIVITIES_IN_CURRICULUM_TYPE_OPTIONS}
              name="degreeActivitiesInCurriculumType"
              disabled={readOnly || hasActivitiesChecked}
              isClearable={false}
              rules={{
                required: msgValidations.required,
              }}
            />
          </Col>
          {degreeActivitiesInCurriculumType?.value ===
            DegreeActivitiesInCurriculumTypeOptions.Grouped && (
            <Col xs={12} md={6}>
              <TextInput
                name="degreeActivitiesInCurriculumGroup"
                label={t(`${prefix}.degreeActivitiesInCurriculumGroup`)}
                control={control}
                rules={{
                  validate: validateTextNotEmpty,
                  required: msgValidations.required,
                  maxLength: {
                    value: 200,
                    message: t('common.validations.maxLength', {
                      length: 50,
                    }),
                  },
                }}
                disabled={readOnly || hasActivitiesChecked}
              />
            </Col>
          )}
        </Row>

        {!readOnly && (
          <ModalConfirmation
            open={showModalDelete}
            title={t(`${prefix}.modalTrash.title`)}
            subtitle={t(`${prefix}.modalTrash.subtitle`)}
            actionText={t(`${prefix}.modalTrash.btn.unLink`)}
            onClose={() => {
              setShowModalDelete(!showModalDelete);
            }}
            onConfirm={() => {
              handleRemoveActivity({ curriculumDegreeActivityCode });
            }}
          />
        )}

        <Row>
          <Col xs={12} md={12}>
            <TableCurriculumActivity
              readOnly={readOnly}
              isLoadingResults={isLoadingTable}
              data={curriculumActivities}
              totalPages={totalPages}
              totalItems={totalCurriculumDegreeActivities}
              currentPage={currentPage}
              onChangePage={(page) => {
                setCurrentPage(page);
              }}
              onDeleteCurriculumActivity={(code: string | number) => {
                setCurriculumDegreeActivityCode(code);
                setShowModalDelete(true);
              }}
              studyPlanTitleName={studyPlanTitles.degreeTitle}
              studyPlanDegreeName={studyPlanTitles.academicDegree}
              showRequirementType={!requirementTypeConfig.isSingle}
            />
            {errors.curriculumActivities && (
              <span className="text-danger d-block text-right pt-2">
                {errors.curriculumActivities.message}
              </span>
            )}
          </Col>
        </Row>
      </div>
    </Suspense>
  );
};
